// Squads (#6780): the client over #7010's `/v1/squads`, which the settings screen
// (`components/SquadSettings.tsx`) reads and writes. The design is
// `docs/design/fleet-redesign/squads.md` (D0-D12).
//
// THE NAME RULE AND THE REPO LIMIT ARE THE DOMAIN'S. `parseSquadName` (`@developerz/domain`)
// is the one D6 rule the API's control calls too, and every write that carries a name runs it here
// BEFORE the request: a name the server would 422 never leaves the browser, and its
// refusal names the part of the rule it broke. A write naming more than `SQUAD_REPOS_MAX`
// repos (the server's 422 `squad_repos_too_many`) is refused here the same way. Nothing
// here restates either rule.
//
// ONE READ FEEDS BOTH THE TABLE AND THE PICKER. `GET /v1/squads` answers the WHOLE set
// (`next_cursor` always null): every named squad with its repos, an empty one included
// (D8), then each enabled repo in no squad as an implicit entry. So the picker needs no
// second read and no paging over `/v1/repos`, and it offers exactly what the server
// listed: a disabled repo in no squad has no implicit entry, so it is not offered.
//
// A REFUSAL IS FRAMED BY ITS `code`, never by status: a 404 is either the squad
// (`squad_not_found`) or a repo (`repo_not_found`), two different sentences. Every other
// refusal (a 403 above all) keeps the server's own `detail`, via `errorReason`.

import { parseSquadName, SQUAD_REPOS_MAX, type SquadNameRefusal } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { ApiError, apiFetch, UserFacingError } from './api';
import { SESSION_TOKEN } from './session';

/**
 * One squad as `/v1/squads` serves it (`squadWire` / `squadBoardWire`,
 * `packages/mcp/src/army/squad.ts`): `id`, `created_at` and `updated_at` are null for an
 * implicit entry. Non-strict, so a field the server gains never breaks a bundle.
 */
const SquadWireSchema = z.object({
  key: z.string(),
  id: z.string().nullable(),
  name: z.string(),
  implicit: z.boolean(),
  repos: z.array(z.string()),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type SquadWire = z.infer<typeof SquadWireSchema>;

/** A named squad: the rows the table lists and the writes address. */
export type NamedSquad = SquadWire & { readonly id: string };

const SquadPageSchema = z.object({ data: z.array(SquadWireSchema) });
const SquadBodySchema = z.object({ squad: SquadWireSchema });

export const SQUADS_QUERY_KEY = 'squads';

/** The account's squads, named then implicit, as the server ordered them. */
export function useSquads(token: string | null = SESSION_TOKEN) {
  return createQuery<readonly SquadWire[]>(() => ({
    queryKey: [SQUADS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () =>
      (await apiFetch('/v1/squads', token, undefined, { schema: SquadPageSchema })).data,
  }));
}

/** The named squads — the table's rows. */
export function namedSquads(rows: readonly SquadWire[]): NamedSquad[] {
  return rows.filter((r): r is NamedSquad => !r.implicit && r.id !== null);
}

/** How many repos stand as their own squad. */
export function implicitCount(rows: readonly SquadWire[]): number {
  return rows.filter((r) => r.implicit).length;
}

/** One repo the picker offers, and the OTHER named squad holding it (`null`: pickable). */
export type PickerRepo = { readonly fullName: string; readonly heldBy: string | null };

/**
 * Every repo of the read, by name. A repo in another named squad is held by it (D1: one
 * squad per repo, never moved); the edited squad's own repos and the implicit entries'
 * are free. `editingId` is the squad being edited, or `null` for a new one.
 */
export function pickerRepos(rows: readonly SquadWire[], editingId: string | null): PickerRepo[] {
  return rows
    .flatMap((row) =>
      row.repos.map((fullName) => ({
        fullName,
        heldBy: row.implicit || row.id === editingId ? null : row.name,
      })),
    )
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
}

/**
 * A refused squad write, carrying WHERE it renders: under the Name field (`name`) or
 * as the form's refusal (`form`). `gone` marks the squad itself vanished (404), which
 * the screen answers by re-reading the list. A `UserFacingError`, so `errorReason`
 * shows its sentence.
 */
export class SquadWriteError extends UserFacingError {
  constructor(
    message: string,
    readonly field: 'name' | 'form',
    readonly gone = false,
  ) {
    super(message);
    this.name = 'SquadWriteError';
  }
}

/** Which part of D6 a refused name broke, as the Name field's error. */
const NAME_RULE_COPY: Readonly<Record<SquadNameRefusal, string>> = {
  empty: 'squads.name_empty',
  too_long: 'squads.name_too_long',
  bad_characters: 'squads.name_bad_characters',
};

/** The rule a refused name broke; a reason this bundle does not know states the whole rule. */
function nameRuleError(why: unknown): SquadWriteError {
  const key =
    typeof why === 'string' && Object.hasOwn(NAME_RULE_COPY, why)
      ? NAME_RULE_COPY[why as SquadNameRefusal]
      : 'squads.name_rule';
  return new SquadWriteError(t(key), 'name');
}

/** The trimmed name the server stores, or the refusal — thrown before any request. */
function checkedName(raw: string): string {
  const parsed = parseSquadName(raw);
  if (!parsed.ok) throw nameRuleError(parsed.why);
  return parsed.name;
}

const text = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The write's repo limit: the server's `max` when it sent one, else the domain's. */
const reposTooMany = (max: unknown): SquadWriteError =>
  new SquadWriteError(
    t('squads.error_repos_too_many', { max: typeof max === 'number' ? max : SQUAD_REPOS_MAX }),
    'form',
  );

/** The repos, or the refusal — thrown before any request, as the server would 422 it. */
function checkedRepos(repos: readonly string[]): readonly string[] {
  if (repos.length > SQUAD_REPOS_MAX) throw reposTooMany(SQUAD_REPOS_MAX);
  return repos;
}

/** A squads route refusal, framed by its `code`; anything else passes through untouched. */
function framed(err: unknown, name: string | undefined): unknown {
  if (!(err instanceof ApiError)) return err;
  const ext = err.extensions ?? {};
  switch (ext['code']) {
    case 'repo_in_another_squad':
      return new SquadWriteError(
        t('squads.error_repo_in_another_squad', {
          repo: text(ext['repo']),
          squad: text(ext['squad_name']),
        }),
        'form',
      );
    case 'squad_name_taken':
      return new SquadWriteError(t('squads.error_name_taken', { name: name ?? '' }), 'name');
    case 'squad_name_invalid':
      return nameRuleError(ext['reason']);
    case 'squad_repos_too_many':
      return reposTooMany(ext['max']);
    case 'squad_not_found':
      return new SquadWriteError(t('squads.error_gone'), 'form', true);
    case 'repo_not_found': {
      const repos = Array.isArray(ext['repos']) ? ext['repos'].map(text).join(', ') : '';
      return new SquadWriteError(t('squads.error_repo_gone', { repos }), 'form');
    }
    default:
      return err;
  }
}

async function send<T>(name: string | undefined, write: () => Promise<T>): Promise<T> {
  try {
    return await write();
  } catch (err) {
    throw framed(err, name);
  }
}

/** `POST /v1/squads`. The name is checked (and trimmed) before the request. */
export async function createSquad(
  body: { readonly name: string; readonly repos: readonly string[] },
  token: string | null = SESSION_TOKEN,
): Promise<SquadWire> {
  const name = checkedName(body.name);
  const repos = checkedRepos(body.repos);
  return send(name, async () => {
    const res = await apiFetch(
      '/v1/squads',
      token,
      { method: 'POST', body: JSON.stringify({ name, repos }) },
      { schema: SquadBodySchema },
    );
    return res.squad;
  });
}

/**
 * `PATCH /v1/squads/:id` with the changed half or both, in one transaction. A name, when
 * sent, is checked (and trimmed) before the request.
 */
export async function updateSquad(
  id: string,
  body: { readonly name?: string; readonly repos?: readonly string[] },
  token: string | null = SESSION_TOKEN,
): Promise<SquadWire> {
  const name = body.name === undefined ? undefined : checkedName(body.name);
  const repos = body.repos === undefined ? undefined : checkedRepos(body.repos);
  return send(name, async () => {
    const res = await apiFetch(
      `/v1/squads/${encodeURIComponent(id)}`,
      token,
      {
        method: 'PATCH',
        body: JSON.stringify({
          ...(name !== undefined ? { name } : {}),
          ...(repos !== undefined ? { repos } : {}),
        }),
      },
      { schema: SquadBodySchema },
    );
    return res.squad;
  });
}

/** `DELETE /v1/squads/:id` — its repos become their own squads again. */
export async function deleteSquad(id: string, token: string | null = SESSION_TOKEN): Promise<void> {
  await send(undefined, () =>
    apiFetch<void>(`/v1/squads/${encodeURIComponent(id)}`, token, { method: 'DELETE' }),
  );
}
